import {
  AllListingsSortByEnum,
  AllListingsSortOrderEnum,
  BuyingFormatEnum,
} from '@b-stock/search-api-client'

import type { ApiCategory } from '@/components/personalizedCategoryCarousel/apiCategories'
import {
  computeGap,
  isExcludedSeller,
  rankCandidates,
} from '@/helpers/betterOdds/computeGap'
import { getEndTime } from '@/helpers/listing/getEndTime'

import listingSearchQuery, { sortListingsByEndTime } from './listingSearchQuery'
import type { SearchParams, SearchResult } from './listingSearchQuery'

// Candidate pool size fetched before ranking/capping to `limit`. Wider than
// any single surface needs so gap/exclusion filtering has enough to work
// with, without fetching the whole category.
const CANDIDATE_POOL_SIZE = 50

// Only surface listings closing soon — a "better odds" prompt on an auction
// with weeks left isn't actionable urgency. Also excludes already-ended
// listings (msRemaining <= 0).
const MAX_MS_REMAINING = 48 * 60 * 60 * 1000

const HOUR_MS = 60 * 60 * 1000

/**
 * Server-side end of the 48h window, rounded UP to the next UTC hour
 * boundary. The rounding keeps the query key stable across renders (and in
 * sync with server-side prefetching) instead of embedding a per-render
 * timestamp; the client-side msRemaining filter below still enforces the
 * exact 48h cutoff inside the slightly-wider fetched pool.
 */
const getMaxEndTime = (now: number): string =>
  new Date(
    Math.ceil((now + MAX_MS_REMAINING) / HOUR_MS) * HOUR_MS
  ).toISOString()

/**
 * The exact `listingSearchQuery` params for a category's candidate pool.
 * Shared with server-side prefetching (see HomeDashboardBand) so the
 * prefetched query key matches this hook's — a mismatched param produces a
 * cache miss and defeats the prefetch silently.
 */
export const getBetterOddsSearchParams = (
  category: ApiCategory | null | undefined,
  now: number = Date.now()
): SearchParams => ({
  params: {
    // Only listings open for bidding: no Buy Now Only, and no Buy Now phase.
    buyingFormat: [BuyingFormatEnum.Auction],
    categories: category ? [category] : [],
    listingType: ['SPOT'],
    sortBy: AllListingsSortByEnum.NumberOfBids,
    sortOrder: AllListingsSortOrderEnum.Asc,
    // Fewest-bids sort picks WHICH 50 we consider; the end-time window keeps
    // that pool from being low-bid listings with weeks left, which would
    // otherwise be filtered to nothing client-side.
    maxEndTime: getMaxEndTime(now),
    limit: CANDIDATE_POOL_SIZE,
  },
  usePostEndpoint: true,
  fetchFilters: false,
})

export type UseBetterOddsCandidatesArgs = {
  /** The focus category to search within (same category as the lost bid). */
  category: ApiCategory | null | undefined
  /** Listing IDs to exclude — the buyer's own active-bid listings. */
  excludeListingIds?: string[]
  /** Max candidates to return, after ranking. */
  limit: number
}

/**
 * Open, same-category listings below their magic number of unique bidders
 * ("gap > 0"), ranked by gap size weighted by urgency, excluding the
 * buyer's own active bids and any excluded sellers. Feeds both the My Bids
 * strip and the homepage carousel — one engine, two presentations.
 *
 * Returns an empty list (not an error state) when `category` is nullish, so
 * callers can render nothing rather than branch on loading/error here.
 */
export const useBetterOddsCandidates = ({
  category,
  excludeListingIds = [],
  limit,
}: UseBetterOddsCandidatesArgs): {
  candidates: SearchResult[]
  isLoading: boolean
} => {
  const excludeSet = new Set(excludeListingIds)

  const { data, isLoading } = listingSearchQuery.useWithOpts(
    [getBetterOddsSearchParams(category)],
    { enabled: Boolean(category) }
  )

  if (!category || !data?.listings.length) {
    return { candidates: [], isLoading }
  }

  const now = Date.now()

  const eligible = data.listings
    .filter((listing) => !excludeSet.has(listing.listingId))
    .filter((listing) => !isExcludedSeller(category, listing.storefrontName))
    .reduce<Array<{ listing: SearchResult; gap: number; msRemaining: number }>>(
      (acc, listing) => {
        const gap = computeGap(category, listing.auction3MP?.uniqueBidders)
        const msRemaining = getEndTime(listing).getTime() - now
        if (
          gap != null &&
          gap > 0 &&
          msRemaining > 0 &&
          msRemaining <= MAX_MS_REMAINING
        ) {
          acc.push({ listing, gap, msRemaining })
        }
        return acc
      },
      []
    )

  // rankCandidates picks WHICH listings make the cut (gap size weighted by
  // urgency) — this re-sorts just that chosen set by end time so what the
  // buyer sees is closing-soonest-first, not the gap/urgency order.
  const candidates = sortListingsByEndTime(
    rankCandidates(eligible)
      .slice(0, limit)
      .map((c) => c.listing)
  )

  return { candidates, isLoading }
}
